import { useEffect, useRef } from "react";
import { useMap } from "react-map-gl/mapbox";
import type { MapMouseEvent } from "react-map-gl/mapbox";

type LayerEventHandler = (e: MapMouseEvent) => void;

/**
 * Subscribe to layer-specific map events with automatic cleanup.
 *
 * @example
 * useMapEvent('click', 'places-layer', (e) => console.log('Layer clicked', e.features));
 */
export function useMapEvent(
  type: "click" | "mouseenter" | "mouseleave" | "mousemove",
  layerId: string,
  handler: LayerEventHandler,
): void {
  const { current: map } = useMap();

  // Use ref to avoid re-subscribing when handler changes
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!map) return;

    const listener: LayerEventHandler = (e) => handlerRef.current(e);

    map.on(type, layerId, listener);
    return () => {
      map.off(type, layerId, listener);
    };
  }, [map, type, layerId]);
}
